import { Prose } from '../../components/Prose'
import { useState } from 'react'
import { useSessions } from '../../api/hooks'
import { logSummary, progress, shortDate, volume, type Session } from '../../lib/session'
import { todayIso } from '../../lib/format'

/**
 * Geçmiş antrenmanlar. Amaç bir "geçmiş listesi" değil, tek soruya cevap:
 * geçen sefer bu hareketi nasıl yaptım, bu sefer daha iyisini yapıyor muyum?
 */
export function SessionHistory() {
  const sessions = useSessions()
  const [open, setOpen] = useState<string | null>(null)

  // Yarım kalan antrenmanlar da listeleniyor. Önceden sadece TAMAMLANMIŞ
  // kayıtlar geliyordu ve "bitir"e basmayı unutan biri için geçmiş sonsuza
  // kadar boş görünüyordu — kayıt duruyor ama ekranda yok. Bugünün devam eden
  // kaydı hariç: o zaten üstteki kartta açık duruyor.
  const rows = (sessions.data ?? [])
    .filter((s) => s.date !== todayIso() || s.completedAt !== null)
    .filter((s) => s.exercises.some((e) => e.sets.some((x) => x.done)))
    .slice(0, 15)

  if (sessions.isLoading) return null

  if (rows.length === 0) {
    return (
      <div className="card">
        <h2>Geçmiş antrenmanlar</h2>
        <p className="sub">
          Henüz kaydedilmiş antrenman yok. İlkinde bir set işaretlediğinde burada birikmeye başlayacak;
          her hareketin yanında "geçen sefer ne yaptın" satırı da o zaman görünür olur.
        </p>
      </div>
    )
  }

  return (
    <div className="card">
      <h2>Geçmiş antrenmanlar</h2>
      <Prose className="card-h">
        Son {rows.length} antrenman. Bir satıra dokunursan o günün hareket hareket dökümü açılır.
      </Prose>

      {rows.map((s) => (
        <div className="day" key={s.date} style={{ marginBottom: 8 }}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer', alignItems: 'baseline' }}
            onClick={() => setOpen(open === s.date ? null : s.date)}
          >
            <div>
              <div className="dt">
                {shortDate(s.date)} · Antrenman {s.day}
                {s.completedAt === null && (
                  <span className="pill" style={{ marginLeft: 6 }}>yarım kaldı</span>
                )}
              </div>
              <div className="dd">{summaryLine(s)}</div>
            </div>
            <span className="pill">{open === s.date ? 'kapat' : 'detay'}</span>
          </div>

          {open === s.date && (
            <div style={{ marginTop: 10, borderTop: '1px solid var(--grid)', paddingTop: 10 }}>
              {s.exercises.map((e, i) => (
                <div key={e.slug + i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', fontSize: 13.5 }}>
                  <span style={{ color: e.skipped ? 'var(--muted)' : 'inherit' }}>
                    {e.name}{e.skipped && ' (atlandı)'}
                  </span>
                  <span style={{ color: 'var(--ink-2)', textAlign: 'right', flex: 'none' }}>
                    {e.skipped ? '—' : logSummary(e)}
                  </span>
                </div>
              ))}
              {s.note && <Prose className="note">{s.note}</Prose>}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function summaryLine(s: Session): string {
  const pr = progress(s)
  const yapilan = s.exercises.filter((e) => !e.skipped).length
  const rpes = s.exercises.map((e) => e.rpe).filter((r): r is number => r !== null)
  const ortRpe = rpes.length ? (rpes.reduce((a, b) => a + b, 0) / rpes.length).toFixed(1) : null
  const toplam = Math.round(s.exercises.reduce((n, e) => n + volume(e), 0))

  return [
    `${yapilan} hareket · ${pr.done} set`,
    toplam > 0 ? `${toplam.toLocaleString('tr-TR')} birim iş` : null,
    ortRpe ? `ort. RPE ${ortRpe}` : null,
  ].filter(Boolean).join(' · ')
}
